import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import Room, { RoomCleanup } from '../Room/Room';
import { useFormikContext } from 'formik';
import { GenericObject } from '@fnol/library/src/lib';
import { Grid, FormHelperText } from '@mui/material';
import { Button, StyledCardSection } from '@fnol/library/src/components';
import { translations } from '../../lib/consts';

interface AddRoomProps {
    inputRoomOpen: boolean;
    setInputRoomOpen: Dispatch<SetStateAction<boolean>>;
    inputRoomId: number;
    setInputRoomId: Dispatch<SetStateAction<number>>;
    addPossible: boolean;
    setAddPossible: Dispatch<SetStateAction<boolean>>;
}

const AddRoom = ({
    inputRoomOpen,
    setInputRoomOpen,
    inputRoomId,
    setInputRoomId,
    addPossible,
    setAddPossible,
}: AddRoomProps) => {
    const { values, setErrors, setTouched, setFieldValue } = useFormikContext<GenericObject>();

    const handleCancel = () => {
        RoomCleanup(setFieldValue);
        setInputRoomId(0);
        setInputRoomOpen(false);
    };

    const checkNextRoomId = () => {
        let availableIds = [0];
        if (values.damagedRooms && values.damagedRooms === 1) availableIds = [1];
        else if (values.damagedRooms && values.damagedRooms === 2) availableIds = [1, 2];
        else if (values.damagedRooms && values.damagedRooms === 3) availableIds = [1, 2, 3];
        let nextAvailableId = 0;
        availableIds.map((availableId) => {
            if (!values[`room${availableId}Area`] && nextAvailableId === 0) {
                nextAvailableId = availableId;
            }
        });
        return nextAvailableId;
    };

    const saveRoomToId = (idToSaveTo: number) => {
        setFieldValue(`room${idToSaveTo}Type`, values.roomType);
        setFieldValue(`room${idToSaveTo}Area`, values.roomArea);
        setFieldValue(`room${idToSaveTo}IsDamageOnCeiling`, values.roomIsDamageOnCeiling);
        setFieldValue(`room${idToSaveTo}NumberOfDemagedWalls`, values.roomNumberOfDemagedWalls);
        setFieldValue(`room${idToSaveTo}WallFinishType`, values.roomWallFinishType);
        setFieldValue(`room${idToSaveTo}Note`, values.roomNote);

        if (idToSaveTo === values.damagedRooms) {
            setAddPossible(false);
        }

        RoomCleanup(setFieldValue);
        setInputRoomId(0);
        setInputRoomOpen(false);
    };

    const handleSave = (idToEdit: number) => {
        let valid = true;
        const existingErrors: GenericObject = {};
        setTouched(
            {
                roomType: true,
                roomArea: true,
                roomIsDamageOnCeiling: true,
                roomNumberOfDemagedWalls: true,
                roomWallFinishType: true,
                roomNote: true,
            },
            false,
        );

        if (!values.roomType) {
            existingErrors.roomType = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }

        if (!values.roomArea) {
            existingErrors.roomArea = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        } else if (isNaN(values.roomArea)) {
            existingErrors.roomArea = translations.validationMessages.NaN;
            valid = false;
        } else if (values.roomArea >= 100) {
            existingErrors.roomArea = translations.validationMessages.AREA_MAX_SIZE;
            valid = false;
        }

        if (!values.roomIsDamageOnCeiling) {
            existingErrors.roomIsDamageOnCeiling = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }

        if (!values.roomNumberOfDemagedWalls && values.roomNumberOfDemagedWalls != '0') {
            existingErrors.roomNumberOfDemagedWalls = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }

        if (!values.roomWallFinishType && values.roomNumberOfDemagedWalls != '0') {
            existingErrors.roomWallFinishType = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }

        setErrors(existingErrors);

        if (valid) {
            const availableId = checkNextRoomId();
            if (idToEdit === 0 && availableId !== 0) {
                saveRoomToId(availableId);
            } else {
                if (idToEdit !== 0) {
                    saveRoomToId(idToEdit);
                } else {
                    return false;
                }
            }
        }
    };

    useEffect(() => {
        setInputRoomOpen(false);
        RoomCleanup(setFieldValue);
        const availableId = checkNextRoomId();
        if (availableId === 0) {
            setAddPossible(false);
        }
    }, []);

    return (
        <>
            <Grid container justifyContent="center">
                <Button
                    id="setInputRoomOpen"
                    onClickHandler={() => setInputRoomOpen(true)}
                    label={translations.addRoom.ADD_NEW}
                    disabled={inputRoomOpen || !addPossible}
                    fullWidth={false}
                />
            </Grid>

            {inputRoomOpen && (
                <>
                    <StyledCardSection>
                        <Room id={inputRoomId} />
                    </StyledCardSection>

                    <Grid container>
                        <Grid container item={true} xs={6}>
                            <Button
                                id="handleCancelRoom"
                                onClickHandler={handleCancel}
                                label={translations.addRoom.CANCEL}
                                fullWidth={false}
                            />
                        </Grid>
                        <Grid container item={true} xs={6} justifyContent="flex-end">
                            <Button
                                id="handleSaveRoom"
                                onClickHandler={() => handleSave(inputRoomId)}
                                label={translations.addRoom.SAVE}
                                fullWidth={false}
                            />
                        </Grid>
                    </Grid>
                </>
            )}
        </>
    );
};

export default AddRoom;
